// Lo que está pasando en la tienda ahora mismo.
//
// Dos trabajos: contar al servidor cuando alguien mete algo en su cesta, y
// enseñar lo que meten las demás. Quien mira ve moverse la tienda, que es lo
// que pasa al entrar a un local y ver gente comprando.
//
// Todo lo que sale de aquí ocurrió de verdad. Si no hay nadie comprando, no
// sale nada — y eso es correcto: una tienda vacía que finge estar llena se
// nota, y además es publicidad engañosa.
//
// Nunca se dice quién. Ni nombre, ni ciudad. Sólo la prenda y cuándo.

const VISIBLE = 5200;          // lo que se queda cada aviso
const ENTRE_AVISOS = 2600;     // aire entre uno y otro: en fila, no encimados
const AL_LLEGAR = 4000;        // lo que se espera antes del primero
const MAX_EN_COLA = 3;

// Identificador de esta pestaña, sólo para que el servidor no me devuelva mis
// propias añadidas. No sale del navegador y no identifica a nadie.
function miIdentificador() {
  try {
    let id = sessionStorage.getItem('sc-vis');
    if (!id) {
      id = Math.random().toString(36).slice(2, 12);
      sessionStorage.setItem('sc-vis', id);
    }
    return id;
  } catch {
    return Math.random().toString(36).slice(2, 12);   // en privado no hay dónde guardar
  }
}

// «hace un momento», «hace 12 minutos». Tiene que ser verdad: es la diferencia
// entre prueba social y cuento.
function hace(cuando) {
  const s = Math.max(0, Math.round((Date.now() - Number(cuando)) / 1000));
  if (s < 90) return 'hace un momento';
  const m = Math.round(s / 60);
  if (m < 60) return `hace ${m} minutos`;
  const h = Math.round(m / 60);
  return h === 1 ? 'hace una hora' : `hace ${h} horas`;
}

class EnVivo extends HTMLElement {
  connectedCallback() {
    this.servidor = this.dataset.servidor;
    if (!this.servidor) return;

    this.yo = miIdentificador();
    this.cola = [];
    this.panel = this.querySelector('[data-panel]');

    this.querySelector('[data-cerrar]')?.addEventListener('click', () => this.callar());

    // Contar lo que se añade aquí. Se cuelga del mismo evento que el aviso de
    // «añadida a tu cesta», así que no toca nada del carrito.
    document.addEventListener('cart:update', (e) => this.contar(e.detail));

    // Escuchar lo que añaden las demás. Se espera un poco: nada más entrar, la
    // clienta está mirando la foto grande, no una esquina.
    setTimeout(() => this.escuchar(), AL_LLEGAR);
  }

  contar(detalle = {}) {
    const item = detalle.item;
    if (!item) return;
    const cuerpo = JSON.stringify({
      titulo: item.product_title || item.title || '',
      talla: item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '',
      foto: item.image || item.featured_image?.url || '',
      url: item.url ? new URL(item.url, location.origin).pathname : '',
      de: this.yo,
    });
    // Va como text/plain, no como application/json, y eso no es un descuido.
    //
    // El servidor está en otro dominio que la tienda. Con application/json el
    // navegador exige pedir permiso antes de mandar, y sendBeacon no sabe
    // hacer esa pregunta: descarta el envío en silencio, sin error, sin nada
    // en la consola. Con text/plain no hace falta permiso previo y sale.
    // El cuerpo sigue siendo JSON; el servidor lo interpreta igual.
    try {
      // Sin esperar respuesta: contar esto jamás puede entorpecer una compra.
      navigator.sendBeacon?.(`${this.servidor}/tienda/api/anadida`, new Blob([cuerpo], { type: 'text/plain;charset=UTF-8' }));
    } catch {
      /* si no se puede contar, no pasa nada */
    }
  }

  escuchar() {
    try {
      this.canal = new EventSource(`${this.servidor}/tienda/api/actividad?de=${encodeURIComponent(this.yo)}`);
    } catch {
      return;
    }
    this.canal.addEventListener('anadida', (e) => this.encolar(JSON.parse(e.data)));
    this.canal.addEventListener('previas', (e) => {
      // De lo de antes, sólo la más reciente. Vaciarle encima cinco avisos a
      // quien acaba de entrar parece un anuncio, no una tienda.
      const previas = JSON.parse(e.data);
      if (previas.length) this.encolar(previas[0]);
    });
    // Si el servidor no está, EventSource reintenta solo. No hay que hacer
    // nada, y desde luego no hay que inventarse actividad para rellenar.
  }

  encolar(dato) {
    if (!dato?.titulo) return;
    if (this.cola.length >= MAX_EN_COLA) return;    // se acumuló: se dejan pasar
    this.cola.push(dato);
    if (!this.pintando) this.siguiente();
  }

  siguiente() {
    const dato = this.cola.shift();
    if (!dato) { this.pintando = false; return; }
    this.pintando = true;

    const q = (s) => this.querySelector(s);
    const foto = q('[data-foto]');
    if (dato.foto) { foto.src = dato.foto; foto.hidden = false; } else { foto.hidden = true; }

    q('[data-titulo]').textContent = dato.titulo + (dato.talla ? ` · ${dato.talla}` : '');
    q('[data-cuando]').textContent = hace(dato.cuando);

    // Comprada pesa más que añadida: la prenda ya no está. Se dice distinto
    // porque es distinto, y quien lo lee sabe leer la diferencia.
    const comprada = dato.que === 'comprada';
    q('.sc-live__lead').textContent = comprada ? 'Acaban de llevársela' : 'Alguien se la está llevando';
    this.dataset.que = comprada ? 'comprada' : 'anadida';

    const enlace = q('[data-enlace]');
    if (dato.url) { enlace.href = dato.url; enlace.removeAttribute('aria-disabled'); }
    else { enlace.removeAttribute('href'); enlace.setAttribute('aria-disabled', 'true'); }

    this.hidden = false;
    requestAnimationFrame(() => this.setAttribute('data-abierto', ''));

    clearTimeout(this.reloj);
    this.reloj = setTimeout(() => {
      this.removeAttribute('data-abierto');
      setTimeout(() => {
        this.hidden = true;
        setTimeout(() => this.siguiente(), ENTRE_AVISOS);
      }, 300);
    }, VISIBLE);
  }

  /** Quien lo cierra no quiere más en esta visita. */
  callar() {
    clearTimeout(this.reloj);
    this.cola.length = 0;
    this.pintando = false;
    this.removeAttribute('data-abierto');
    this.hidden = true;
    this.canal?.close();
  }

  disconnectedCallback() {
    clearTimeout(this.reloj);
    this.canal?.close();
  }
}

if (!customElements.get('sc-live')) {
  customElements.define('sc-live', EnVivo);
}
